import { Box, Button, Field, VStack } from '@chakra-ui/react'
import { CitySection } from 'commons/components/city'
import { CountrySection } from 'commons/components/country'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { Label } from 'commons/components/layout/Label'
import { Section } from 'commons/components/layout/Section'
import { ZipCodeSection } from 'commons/components/zipCode'
import 'commons/design-system/PlaceAutocomplete.css'
import { BgColors } from 'commons/design-system/tokens'
import { FormStep } from 'commons/types/paymentTypes'
import { getPlaceDetails } from 'commons/utils/googleServices'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlacesAutocomplete, { type Suggestion } from 'react-places-autocomplete'

export type PlacesAutocompleteFieldProps = {
	errors: {
		zipcode?: string
		city?: string
		country?: string
	}
	isLoading: boolean
	step: FormStep
	isReadOnly: boolean
	city?: string
	zipcode?: string
	country?: string
	onZipcodeChange?: (value: string) => void
	onCityChange?: (value: string) => void
	onCountryChange?: (value: string) => void
	onClearError?: (
		field: 'firstname' | 'lastname' | 'birthdate' | 'zipcode' | 'city' | 'country'
	) => void
	onManualModeChange?: (isManual: boolean) => void
	isManualMode?: boolean
	label: string
	hideZipAndCity?: boolean
}

export const PlacesAutocompleteField = ({
	errors,
	isLoading,
	step,
	isReadOnly,
	city = '',
	zipcode = '',
	country = '',
	onZipcodeChange,
	onCityChange,
	onCountryChange,
	onClearError,
	onManualModeChange,
	isManualMode = false,
	label,
	hideZipAndCity = false
}: PlacesAutocompleteFieldProps) => {
	const { t } = useTranslation()
	const [inputValue, setInputValue] = useState(zipcode)
	const previousCountryRef = useRef(country)

	// Sync inputValue with zipcode prop when it changes externally
	useEffect(() => {
		if (zipcode !== inputValue && !inputValue.includes(zipcode)) {
			setInputValue(zipcode)
		}
	}, [zipcode])

	// Switch to manual mode when there are errors on city or zipcode
	useEffect(() => {
		if ((errors.city || errors.zipcode) && !isManualMode) {
			onManualModeChange?.(true)
		}
	}, [errors.city, errors.zipcode, isManualMode, onManualModeChange])

	// Reset input value when country changes (but not on initial mount)
	useEffect(() => {
		if (
			previousCountryRef.current !== country &&
			previousCountryRef.current !== '' &&
			!isManualMode
		) {
			setInputValue('')
			onZipcodeChange?.('')
			onCityChange?.('')
		}
		previousCountryRef.current = country
	}, [country, onZipcodeChange, onCityChange, isManualMode])

	const searchOptions = useMemo(() => {
		const options: { types: string[]; componentRestrictions?: { country: string } } = {
			types: ['postal_code']
		}
		if (country) {
			options.componentRestrictions = { country: country.toLowerCase() }
		}
		return options
	}, [country])

	const handleSelect = async (address: string, placeId: string) => {
		setInputValue(address)
		try {
			const place = await getPlaceDetails(placeId)
			if (place.address_components) {
				const addressComponents = place.address_components
				const postalCode = addressComponents.find((c) => c.types.includes('postal_code'))?.long_name
				let cityValue = addressComponents.find((c) => c.types.includes('locality'))?.long_name
				if (!cityValue) {
					cityValue = addressComponents.find((c) => c.types.includes('postal_town'))?.long_name
				}
				onZipcodeChange?.(postalCode ?? '')
				onCityChange?.(cityValue ?? '')
				if (errors.zipcode) {
					onClearError?.('zipcode')
				}
				if (errors.city) {
					onClearError?.('city')
				}
				if (
					postalCode === undefined ||
					cityValue === undefined ||
					postalCode === '' ||
					cityValue === ''
				) {
					toggleManualMode()
				}
			}
		} catch (error) {
			console.error('Error fetching place details:', error)
		}
	}

	const handleChange = (value: string) => {
		setInputValue(value)
		if (value === '') {
			onZipcodeChange?.('')
			onCityChange?.('')
		} else {
			onZipcodeChange?.(value)
		}
		if (errors.zipcode) {
			onClearError?.('zipcode')
		}
	}

	const toggleManualMode = () => {
		onManualModeChange?.(!isManualMode)
	}

	return (
		<VStack align='stretch'>
			<CountrySection
				errors={errors}
				isLoading={isLoading}
				step={step}
				isReadOnly={isReadOnly}
				country={country}
				onCountryChange={onCountryChange}
				onClearError={onClearError}
			/>
			{hideZipAndCity ? null : isManualMode ? (
				<>
					<ZipCodeSection
						errors={errors}
						isLoading={isLoading}
						step={step}
						isReadOnly={isReadOnly}
						zipcode={zipcode}
						onZipcodeChange={onZipcodeChange}
						onClearError={onClearError}
					/>
					<CitySection
						errors={errors}
						isLoading={isLoading}
						step={step}
						isReadOnly={isReadOnly}
						city={city}
						onCityChange={onCityChange}
						onClearError={onClearError}
					/>
				</>
			) : (
				<Field.Root invalid={!!errors.zipcode}>
					<Section px={0} pt={2} pb={0}>
						<Label>{label}</Label>
						<PlacesAutocomplete
							value={inputValue}
							onChange={handleChange}
							onSelect={handleSelect}
							searchOptions={searchOptions}
						>
							{({
								getInputProps,
								suggestions,
								getSuggestionItemProps,
								loading
							}: {
								getInputProps: (options?: object) => object
								suggestions: readonly Suggestion[]
								getSuggestionItemProps: (suggestion: Suggestion, options?: object) => object
								loading: boolean
							}) => (
								<Box className='autocomplete-container'>
									<input
										{...getInputProps({
											placeholder: '',
											className: 'pac-target-input',
											autocomplete: 'postal-code',
											disabled:
												isLoading || step === FormStep.STEP_BIRTHDATE || isReadOnly || !country
										})}
										style={{
											border: errors.zipcode
												? '2px solid rgb(229, 62, 62)'
												: `1px solid ${BgColors.Z3}`
										}}
									/>
									{suggestions.length > 0 && (
										<Box className='autocomplete-dropdown-container'>
											{loading && <Box p={2}>Loading...</Box>}
											{suggestions.map((suggestion: Suggestion) => (
												<Box
													{...getSuggestionItemProps(suggestion)}
													key={suggestion.placeId}
													className={suggestion.active ? 'pac-item pac-item-selected' : 'pac-item'}
												>
													{suggestion.description}
												</Box>
											))}
										</Box>
									)}
								</Box>
							)}
						</PlacesAutocomplete>
						{errors.zipcode && <FormErrors withoutBorder errors={[errors.zipcode]} />}
						{!isReadOnly && (
							<Button
								variant='link'
								fontFamily='Montserrat'
								fontWeight='600'
								fontSize='14px'
								mt={3}
								onClick={toggleManualMode}
								disabled={isLoading || isReadOnly}
							>
								{t('phone_form.verify_identity_enter_manually')}
							</Button>
						)}
					</Section>
				</Field.Root>
			)}
		</VStack>
	)
}
